import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

const TableDropdown = ({ isVisible, position, options, onClose }) => {
  const dropdownRef = useRef(null);
  const [adjustedPosition, setAdjustedPosition] = useState(position);

  // Calculate optimal position to prevent overflow
  const calculatePosition = () => {
    if (!position || !dropdownRef.current) return position;

    const dropdownWidth = 224; // w-56 = 14rem = 224px
    const dropdownHeight = options.length * 40 + 8; // Approximate height
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;

    let adjustedLeft = position.left;
    let adjustedTop = position.top;

    // Adjust horizontal position to prevent right overflow
    if (adjustedLeft + dropdownWidth > viewportWidth + scrollX) {
      adjustedLeft = viewportWidth + scrollX - dropdownWidth - 8; // 8px margin
    }

    // Ensure it doesn't go off the left edge
    if (adjustedLeft < scrollX + 8) {
      adjustedLeft = scrollX + 8;
    }

    // Adjust vertical position to prevent bottom overflow
    if (adjustedTop + dropdownHeight > viewportHeight + scrollY) {
      // Position above the trigger button instead
      adjustedTop = position.top - dropdownHeight - 4;
    }

    // Ensure it doesn't go off the top edge
    if (adjustedTop < scrollY + 8) {
      adjustedTop = scrollY + 8;
    }

    return {
      top: adjustedTop,
      left: adjustedLeft,
    };
  };

  // Update position when dropdown becomes visible
  useEffect(() => {
    if (isVisible && position) {
      // Small delay to ensure the ref is available
      const timer = setTimeout(() => {
        const newPosition = calculatePosition();
        setAdjustedPosition(newPosition);
      }, 0);

      return () => clearTimeout(timer);
    }
  }, [isVisible, position, options.length]);

  // Close the dropdown if clicked outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isVisible, onClose]);

  // Handle window resize to recalculate position
  useEffect(() => {
    const handleResize = () => {
      if (isVisible) {
        const newPosition = calculatePosition();
        setAdjustedPosition(newPosition);
      }
    };

    if (isVisible) {
      window.addEventListener("resize", handleResize);
      window.addEventListener("scroll", handleResize);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleResize);
    };
  }, [isVisible]);

  if (!isVisible || !adjustedPosition) return null;

  return ReactDOM.createPortal(
    <div
      ref={dropdownRef}
      className="absolute w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
      style={{
        top: `${adjustedPosition.top}px`,
        left: `${adjustedPosition.left}px`,
        position: "absolute",
        maxWidth: "calc(100vw - 16px)", // Prevent horizontal overflow
        maxHeight: "calc(100vh - 16px)", // Prevent vertical overflow
      }}
    >
      <ul className="py-1 overflow-y-auto max-h-80">
        {options.filter(option => option.condition !== false).map((option, index) => (
          <li key={index}>
            <button
              className={`flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm transition-colors duration-150 ${
                option.danger
                  ? "text-red-700 hover:bg-red-50 focus:bg-red-50"
                  : "text-gray-700 hover:bg-gray-50 focus:bg-gray-50"
              }`}
              onClick={(e) => {
                e.preventDefault();
                option.onClick();
                onClose();
              }}
            >
              {option.icon && <span className="flex-shrink-0">{option.icon}</span>}
              <span className="flex-1 truncate">{option.label}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>,
    document.body
  );
};

export default TableDropdown;
